import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  ArrowLeft, Download, FileText, LogOut, Plus, Trash2, Users, Loader2, Upload, Star, Mail, Pencil, FileUp,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import api, { API } from "@/lib/api";

function GuideForm({ onCreated }) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [tags, setTags] = useState("");
  const [file, setFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [open, setOpen] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!title || !description || !file) {
      toast.error("Title, description, and a PDF file are all required.");
      return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("title", title);
      fd.append("description", description);
      fd.append("tags", tags);
      fd.append("pdf", file);
      await api.post("/admin/guides", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Guide added.");
      setTitle(""); setDescription(""); setTags(""); setFile(null);
      setOpen(false);
      onCreated?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          data-testid="add-guide-btn"
          className="rounded-full bg-burgundy hover:bg-burgundy-700 text-white h-10 px-5"
        >
          <Plus className="h-4 w-4" /> New guide
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-lg bg-cream border-sand rounded-2xl">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl text-burgundy-ink">Add a new guide</DialogTitle>
          <DialogDescription>Title, one-line description, and the PDF you want to send.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 mt-2">
          <div className="space-y-1.5">
            <Label htmlFor="title" className="text-xs uppercase tracking-[0.14em] text-burgundy">Title</Label>
            <Input
              data-testid="new-guide-title"
              id="title" value={title} onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. The 30-second hook audit"
              className="h-11 rounded-xl bg-white border-sand focus-visible:ring-burgundy"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="desc" className="text-xs uppercase tracking-[0.14em] text-burgundy">One-line description</Label>
            <Textarea
              data-testid="new-guide-desc"
              id="desc" value={description} onChange={(e) => setDescription(e.target.value)}
              placeholder="What will they learn? Keep it to one punchy line."
              className="min-h-[72px] rounded-xl bg-white border-sand focus-visible:ring-burgundy"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="tags" className="text-xs uppercase tracking-[0.14em] text-burgundy">Tags <span className="text-foreground/50 normal-case tracking-normal">(comma-separated, e.g. hooks, growth)</span></Label>
            <Input
              data-testid="new-guide-tags"
              id="tags" value={tags} onChange={(e) => setTags(e.target.value)}
              placeholder="hooks, storytelling, growth"
              className="h-11 rounded-xl bg-white border-sand focus-visible:ring-burgundy"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-[0.14em] text-burgundy">PDF file</Label>
            <label className="flex items-center gap-3 cursor-pointer rounded-xl border border-dashed border-sand bg-white px-4 py-4 hover:bg-blush/40 transition-colors">
              <Upload className="h-4 w-4 text-burgundy" />
              <span className="text-sm text-foreground/70 flex-1 truncate">
                {file ? file.name : "Click to upload a PDF (max 20MB)"}
              </span>
              <input
                data-testid="new-guide-file"
                type="file" accept="application/pdf" className="hidden"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
              />
            </label>
          </div>
          <Button
            data-testid="submit-new-guide-btn"
            type="submit" disabled={submitting}
            className="w-full h-11 rounded-xl bg-burgundy hover:bg-burgundy-700 text-white"
          >
            {submitting ? <><Loader2 className="h-4 w-4 animate-spin" /> Uploading…</> : "Publish guide"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function EmailPreviewDialog({ guide, onOpenChange }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!guide) return;
    setLoading(true); setErr("");
    api.get(`/admin/guides/${guide.id}/email-preview`)
      .then((res) => setData(res.data))
      .catch((e) => setErr(e?.response?.data?.detail || "Failed to load preview"))
      .finally(() => setLoading(false));
  }, [guide]);

  return (
    <Dialog open={!!guide} onOpenChange={(v) => !v && onOpenChange(null)}>
      <DialogContent
        data-testid="email-preview-dialog"
        className="sm:max-w-2xl bg-cream border-sand rounded-2xl max-h-[85vh] flex flex-col"
      >
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl text-burgundy-ink">Email preview</DialogTitle>
          <DialogDescription>
            This is the exact email a subscriber receives after they enter their address for <strong>{guide?.title}</strong>.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-10 flex items-center justify-center text-burgundy/70">
            <Loader2 className="h-4 w-4 animate-spin mr-2" /> Rendering…
          </div>
        ) : err ? (
          <p className="text-red-700 text-sm">{err}</p>
        ) : (
          <div className="flex-1 overflow-hidden flex flex-col gap-3">
            <div className="rounded-xl border border-sand bg-white p-4 text-sm space-y-1">
              <p><span className="uppercase text-[10px] tracking-[0.14em] text-burgundy/70">From</span> <span data-testid="email-preview-from" className="ml-2 text-foreground/80">{data.from}</span></p>
              <p><span className="uppercase text-[10px] tracking-[0.14em] text-burgundy/70">Subject</span> <span data-testid="email-preview-subject" className="ml-2 font-medium text-burgundy-ink">{data.subject}</span></p>
            </div>
            <div className="flex-1 rounded-xl border border-sand bg-white overflow-hidden">
              <iframe
                data-testid="email-preview-iframe"
                title="Email HTML preview"
                srcDoc={data.html}
                sandbox=""
                className="w-full h-[420px] border-0"
              />
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function EditTagsDialog({ guide, onOpenChange, onSaved }) {
  const [tags, setTags] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (guide) setTags((guide.tags || []).join(", "));
  }, [guide]);

  const save = async () => {
    if (!guide) return;
    setSaving(true);
    try {
      const arr = tags.split(",").map((s) => s.trim().toLowerCase()).filter(Boolean);
      await api.patch(`/admin/guides/${guide.id}`, { tags: arr });
      toast.success("Tags updated.");
      onSaved?.();
      onOpenChange(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update tags");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!guide} onOpenChange={(v) => !v && onOpenChange(null)}>
      <DialogContent data-testid="edit-tags-dialog" className="sm:max-w-md bg-cream border-sand rounded-2xl">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl text-burgundy-ink">Edit tags</DialogTitle>
          <DialogDescription>Comma-separated. Up to 8 tags per guide.</DialogDescription>
        </DialogHeader>
        <Input
          data-testid="edit-tags-input"
          value={tags}
          onChange={(e) => setTags(e.target.value)}
          placeholder="hooks, storytelling, growth"
          className="h-11 rounded-xl bg-white border-sand focus-visible:ring-burgundy"
        />
        <Button
          data-testid="save-tags-btn"
          onClick={save} disabled={saving}
          className="w-full h-11 rounded-xl bg-burgundy hover:bg-burgundy-700 text-white"
        >
          {saving ? <><Loader2 className="h-4 w-4 animate-spin" /> Saving…</> : "Save tags"}
        </Button>
      </DialogContent>
    </Dialog>
  );
}

function GuidesPanel() {
  const [guides, setGuides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [previewGuide, setPreviewGuide] = useState(null);
  const [editTagsGuide, setEditTagsGuide] = useState(null);
  const [replacingPdfId, setReplacingPdfId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/admin/guides");
      setGuides(res.data);
    } catch {
      toast.error("Failed to load guides");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const del = async (id) => {
    if (!window.confirm("Delete this guide? It will be hidden from the public library.")) return;
    try {
      await api.delete(`/admin/guides/${id}`);
      toast.success("Guide removed.");
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  const toggleFeatured = async (guide) => {
    try {
      const res = await api.post(`/admin/guides/${guide.id}/feature`);
      toast.success(res.data.is_featured ? "Pinned as featured." : "Removed from featured.");
      load();
    } catch {
      toast.error("Couldn't update featured status");
    }
  };

  const replacePdf = async (guide, file) => {
    if (!file) return;
    if (file.type && file.type !== "application/pdf") {
      toast.error("Please choose a PDF file.");
      return;
    }
    setReplacingPdfId(guide.id);
    try {
      const fd = new FormData();
      fd.append("pdf", file);
      await api.post(`/admin/guides/${guide.id}/pdf`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("PDF replaced.");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to replace PDF");
    } finally {
      setReplacingPdfId(null);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <div>
          <h2 className="font-heading text-2xl text-burgundy-ink">Guides</h2>
          <p className="text-sm text-foreground/60">Everything in your public library.</p>
        </div>
        <GuideForm onCreated={load} />
      </div>

      {loading ? (
        <div className="grid gap-3">
          {[0,1,2].map((i) => <div key={i} className="h-16 rounded-xl bg-white border border-sand animate-pulse" />)}
        </div>
      ) : guides.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-sand p-10 text-center bg-white/60">
          <FileText className="h-6 w-6 text-burgundy/50 mx-auto" />
          <p className="mt-3 font-heading text-xl text-burgundy-ink">No guides yet.</p>
          <p className="text-sm text-foreground/60">Add your first guide to fill the library.</p>
        </div>
      ) : (
        <div data-testid="admin-guides-list" className="rounded-2xl border border-sand bg-white overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-blush/40 hover:bg-blush/40">
                <TableHead className="text-burgundy uppercase text-[11px] tracking-[0.14em]">Title</TableHead>
                <TableHead className="text-burgundy uppercase text-[11px] tracking-[0.14em]">Description</TableHead>
                <TableHead className="text-burgundy uppercase text-[11px] tracking-[0.14em]">Tags</TableHead>
                <TableHead className="text-burgundy uppercase text-[11px] tracking-[0.14em]">PDF</TableHead>
                <TableHead className="text-burgundy uppercase text-[11px] tracking-[0.14em] text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {guides.map((g) => (
                <TableRow key={g.id} data-testid={`admin-guide-row-${g.id}`}>
                  <TableCell className="font-heading text-burgundy-ink text-base">
                    <span className="inline-flex items-center gap-2">
                      {g.is_featured && (
                        <span
                          data-testid={`featured-badge-${g.id}`}
                          className="inline-flex items-center gap-1 rounded-full bg-burgundy px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-white"
                        >
                          <Star className="h-2.5 w-2.5 fill-white" /> Featured
                        </span>
                      )}
                      {g.title}
                    </span>
                  </TableCell>
                  <TableCell className="max-w-md text-foreground/70">{g.description}</TableCell>
                  <TableCell data-testid={`admin-guide-tags-${g.id}`}>
                    <div className="flex flex-wrap items-center gap-1">
                      {(g.tags || []).length === 0 ? (
                        <span className="text-xs text-foreground/40">—</span>
                      ) : (
                        (g.tags || []).map((t) => (
                          <span
                            key={t}
                            className="inline-flex items-center rounded-full bg-blush/70 px-2 py-0.5 text-[10px] font-medium text-burgundy"
                          >
                            #{t}
                          </span>
                        ))
                      )}
                      <button
                        type="button"
                        data-testid={`edit-tags-btn-${g.id}`}
                        onClick={() => setEditTagsGuide(g)}
                        className="inline-flex items-center gap-0.5 rounded-full text-[10px] text-burgundy/60 hover:text-burgundy px-1"
                      >
                        <Pencil className="h-3 w-3" />
                      </button>
                    </div>
                  </TableCell>
                  <TableCell>
                    {g.has_pdf
                      ? <span className="inline-flex items-center gap-1.5 text-xs text-burgundy"><FileText className="h-3.5 w-3.5" /> {g.original_filename}</span>
                      : <span className="text-xs text-foreground/50">missing</span>}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Button
                        data-testid={`preview-email-btn-${g.id}`}
                        onClick={() => setPreviewGuide(g)}
                        variant="ghost" size="sm"
                        className="rounded-full text-burgundy hover:text-white hover:bg-burgundy"
                      >
                        <Mail className="h-4 w-4" /> Preview email
                      </Button>
                      <label
                        data-testid={`replace-pdf-label-${g.id}`}
                        className={
                          (replacingPdfId === g.id
                            ? "opacity-70 cursor-wait "
                            : "cursor-pointer ") +
                          "inline-flex items-center gap-1.5 h-8 px-3 rounded-full text-xs font-medium text-burgundy hover:text-white hover:bg-burgundy transition-colors"
                        }
                      >
                        {replacingPdfId === g.id ? (
                          <><Loader2 className="h-4 w-4 animate-spin" /> Uploading…</>
                        ) : (
                          <><FileUp className="h-4 w-4" /> Replace PDF</>
                        )}
                        <input
                          data-testid={`replace-pdf-input-${g.id}`}
                          type="file"
                          accept="application/pdf"
                          className="sr-only"
                          disabled={replacingPdfId === g.id}
                          onChange={(e) => {
                            const f = e.target.files?.[0];
                            e.target.value = "";
                            if (f) replacePdf(g, f);
                          }}
                        />
                      </label>
                      <Button
                        data-testid={`feature-guide-${g.id}`}
                        onClick={() => toggleFeatured(g)}
                        variant="ghost" size="sm"
                        className={
                          g.is_featured
                            ? "rounded-full bg-burgundy text-white hover:bg-burgundy-700"
                            : "rounded-full text-burgundy hover:text-white hover:bg-burgundy"
                        }
                      >
                        <Star className={`h-4 w-4 ${g.is_featured ? "fill-white" : ""}`} />
                        {g.is_featured ? "Featured" : "Feature"}
                      </Button>
                      <Button
                        data-testid={`delete-guide-${g.id}`}
                        onClick={() => del(g.id)}
                        variant="ghost" size="sm"
                        className="text-burgundy hover:text-white hover:bg-burgundy rounded-full"
                      >
                        <Trash2 className="h-4 w-4" /> Remove
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <EmailPreviewDialog guide={previewGuide} onOpenChange={setPreviewGuide} />
      <EditTagsDialog guide={editTagsGuide} onOpenChange={setEditTagsGuide} onSaved={load} />
    </div>
  );
}

function SubmissionsPanel() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/admin/submissions");
      setRows(res.data);
    } catch {
      toast.error("Failed to load submissions");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) =>
      (r.email || "").toLowerCase().includes(q) ||
      (r.guide_title || "").toLowerCase().includes(q)
    );
  }, [rows, query]);

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-5">
        <div>
          <h2 className="font-heading text-2xl text-burgundy-ink">Email captures</h2>
          <p className="text-sm text-foreground/60">
            {rows.length} total submissions.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Input
            data-testid="submissions-search"
            placeholder="Search email or guide…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="h-10 rounded-xl bg-white border-sand w-64"
          />
          <a href={`${API}/admin/submissions/export`} target="_blank" rel="noreferrer">
            <Button
              data-testid="export-csv-btn"
              className="rounded-full bg-burgundy hover:bg-burgundy-700 text-white h-10 px-5"
            >
              <Download className="h-4 w-4" /> Export CSV
            </Button>
          </a>
        </div>
      </div>

      {loading ? (
        <div className="grid gap-3">
          {[0,1,2].map((i) => <div key={i} className="h-14 rounded-xl bg-white border border-sand animate-pulse" />)}
        </div>
      ) : filtered.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-sand p-10 text-center bg-white/60">
          <Users className="h-6 w-6 text-burgundy/50 mx-auto" />
          <p className="mt-3 font-heading text-xl text-burgundy-ink">No submissions yet.</p>
          <p className="text-sm text-foreground/60">They'll show up here as soon as someone grabs a guide.</p>
        </div>
      ) : (
        <div data-testid="admin-submissions-table" className="rounded-2xl border border-sand bg-white overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-blush/40 hover:bg-blush/40">
                <TableHead className="text-burgundy uppercase text-[11px] tracking-[0.14em]">Submitted</TableHead>
                <TableHead className="text-burgundy uppercase text-[11px] tracking-[0.14em]">Email</TableHead>
                <TableHead className="text-burgundy uppercase text-[11px] tracking-[0.14em]">Guide</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((r) => (
                <TableRow key={r.id} data-testid={`submission-row-${r.id}`}>
                  <TableCell className="text-foreground/70 whitespace-nowrap">
                    {new Date(r.created_at).toLocaleString()}
                  </TableCell>
                  <TableCell className="font-medium">{r.email}</TableCell>
                  <TableCell className="text-foreground/80">{r.guide_title}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}

export default function AdminDashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(location.state?.user || null);
  const [authChecked, setAuthChecked] = useState(Boolean(location.state?.user));

  useEffect(() => {
    if (location.state?.user) return;
    api.get("/auth/me")
      .then((res) => { setUser(res.data); setAuthChecked(true); })
      .catch(() => navigate("/admin/login", { replace: true }));
  }, [navigate, location.state]);

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch { /* proceed to login even if logout API fails */ }
    navigate("/admin/login", { replace: true });
  };

  if (!authChecked || !user) {
    return (
      <div className="min-h-screen grid place-items-center bg-cream">
        <div className="flex items-center gap-2 text-burgundy">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading studio…
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream" data-testid="admin-dashboard">
      <header className="border-b border-sand bg-white/70 backdrop-blur">
        <div className="mx-auto max-w-6xl px-5 md:px-8 py-4 flex items-center justify-between">
          <a href="/" data-testid="admin-back-home" className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.16em] text-burgundy/70 hover:text-burgundy transition-colors">
            <ArrowLeft className="h-3.5 w-3.5" /> Site
          </a>
          <div className="flex items-center gap-3">
            <div className="text-right hidden sm:block">
              <p className="text-sm font-medium text-burgundy-ink" data-testid="admin-user-name">{user.name}</p>
              <p className="text-xs text-foreground/60">{user.email}</p>
            </div>
            {user.picture && (
              <img src={user.picture} alt="" className="h-9 w-9 rounded-full border border-sand" referrerPolicy="no-referrer" />
            )}
            <Button
              data-testid="logout-btn"
              onClick={logout}
              variant="outline"
              className="rounded-full border-sand bg-white hover:bg-blush hover:text-burgundy h-9 px-4 text-burgundy"
            >
              <LogOut className="h-4 w-4" /> Sign out
            </Button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-5 md:px-8 py-10">
        <div className="mb-8">
          <p className="text-[11px] font-medium uppercase tracking-[0.16em] text-burgundy">Studio</p>
          <h1 className="mt-2 font-heading text-4xl md:text-5xl text-burgundy-ink">Behind the Story · Admin</h1>
        </div>

        <Tabs defaultValue="guides" className="w-full">
          <TabsList className="bg-white border border-sand rounded-full p-1 h-11">
            <TabsTrigger
              data-testid="tab-guides"
              value="guides"
              className="rounded-full px-5 h-9 data-[state=active]:bg-burgundy data-[state=active]:text-white text-burgundy"
            >
              <FileText className="h-4 w-4" /> Guides
            </TabsTrigger>
            <TabsTrigger
              data-testid="tab-submissions"
              value="submissions"
              className="rounded-full px-5 h-9 data-[state=active]:bg-burgundy data-[state=active]:text-white text-burgundy"
            >
              <Users className="h-4 w-4" /> Submissions
            </TabsTrigger>
          </TabsList>
          <TabsContent value="guides" className="mt-8"><GuidesPanel /></TabsContent>
          <TabsContent value="submissions" className="mt-8"><SubmissionsPanel /></TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
